import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, LoadingScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, qk } from '@cowoo/shared';
import { api } from '../../lib/api';

/**
 * `/grundrisse/neu` — "Grundriss anlegen" (10-web-admin § 5.4).
 *
 * A route, not a modal on the overview: § 4 lists it as one, it is deep-linkable
 * that way, and the button on the overview navigated here long before anything
 * answered — which is what a member of staff found by clicking it.
 *
 * The plan starts empty and the server creates its first draft, so the next
 * step is the upload of § 6.2 rather than an editor with nothing to edit.
 */
export function CreatePlan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  // `['admin','locations']` caches the *items*, as every other reader of the
  // key does; caching the envelope here left the "Gebäude" select empty
  // whenever the list screen had filled the cache first.
  const locations = useQuery({
    queryKey: qk.admin.area('locations'),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations');
      if (error) throw error;
      return data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (body: { locationId: string; name: string; sort: number }) => {
      const { data, error } = await api.POST('/admin/floor-plans', { body });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('plans') });
      navigate(`/grundrisse/${data.id}/upload`);
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      locationId: String(form.get('locationId') ?? ''),
      name: String(form.get('name') ?? '').trim(),
      sort: Number(form.get('sort') ?? 0),
    });
  }

  if (locations.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = locations.data ?? [];

  return (
    <main className="flex justify-center px-6 py-10">
      <form
        onSubmit={submit}
        className="border-line rounded-card flex w-[520px] flex-col gap-4 border bg-white px-6 py-5"
      >
        <h1 className="text-webH3 text-ink">{t('admin.plans.create')}</h1>

        {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-label text-muted uppercase">{t('admin.plans.building')}</span>
          <select
            name="locationId"
            required
            defaultValue={items[0]?.id ?? ''}
            className="border-line-strong rounded-input text-adminBody h-field border bg-white px-3 font-semibold"
          >
            {items.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-label text-muted uppercase">{t('admin.plans.floorName')}</span>
          <input
            name="name"
            required
            maxLength={120}
            placeholder={t('admin.plans.floorNamePlaceholder')}
            className="border-line-strong rounded-input text-adminBody h-field border px-3"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-label text-muted uppercase">{t('admin.plans.sort')}</span>
          <input
            name="sort"
            type="number"
            min={0}
            max={999}
            defaultValue={0}
            className="border-line-strong rounded-input text-adminBody h-field w-28 border px-3"
          />
          <span className="text-adminSmall text-muted">{t('admin.plans.sortHint')}</span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/grundrisse')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="sm" loading={create.isPending} disabled={items.length === 0}>
            {t('admin.plans.createSubmit')}
          </Button>
        </div>
      </form>
    </main>
  );
}
